import { AodRun, AodRunUserStatusEnum } from '@app/modules/audit/modules/aod/aod.types';
import { useAuditReportsTriggerReprocessRunActions } from '@mi-front/api-v2';
import { Alert, AlertTitle, Box } from '@mui/material';
import { useIntl } from 'react-intl';
import { isAodRunMissingMaterialsCompletion } from '../../../../../../utils/aod-run-completion-reason';
import { MissingMaterialsGuidance } from '../MissingMaterialsGuidance';
import { ReprocessAction } from './ReprocessAction';
import { ReprocessCompletion } from './ReprocessCompletion';
import { ReprocessProgress } from './ReprocessProgress';

interface Props {
  run: AodRun;
  isDisabled: boolean;
  isLoading: boolean;
  executeAction: (promise: Promise<unknown>) => void;
}

/**
 * Reprocess advanced action, or guidance when blocked by missing spot materials.
 */
export function ReprocessRun({ run, isDisabled, isLoading, executeAction }: Props) {
  const { $t } = useIntl();
  const reprocessMutation = useAuditReportsTriggerReprocessRunActions();

  const handleReprocess = () => {
    executeAction(
      reprocessMutation.mutateAsync({
        id: run.id,
      }),
    );
  };

  const isReprocessed = run.userStatus === AodRunUserStatusEnum.REPROCESSED;
  const isMissingMaterials = isAodRunMissingMaterialsCompletion(run);

  if (isMissingMaterials && !run.canReprocess) {
    return <MissingMaterialsGuidance />;
  }

  if (!run.canReprocess && !isReprocessed) {
    return null;
  }

  return (
    <Alert severity="warning">
      <AlertTitle>
        {$t({
          id: 'audit.aod.actions.triggerReprocess',
          defaultMessage: 'Reprocesar',
        })}
      </AlertTitle>
      <Box display="flex" flexDirection="column" gap={2}>
        <ReprocessProgress run={run} />
        <ReprocessCompletion run={run} />
        <ReprocessAction
          run={run}
          isDisabled={isDisabled}
          isLoading={isLoading}
          onReprocess={handleReprocess}
        />
      </Box>
    </Alert>
  );
}
